import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Chip, Stack,
  Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, CircularProgress, Alert, TextField, InputAdornment,
  Switch, Tooltip, Collapse, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Snackbar,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  LocationOn as LocationIcon,
  Send as SendIcon,
  Group as TeamIcon,
  LocalOffer as TagIcon,
  Email as EmailOutlineIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import CommScheduleDrawer, { ScheduleData, describeRecurrence } from './CommScheduleDrawer';
import CommEmptyState from './shared/CommEmptyState';
import { commTabHeaderSx, commTableHeadRowSx } from './shared/communicationsStyles';

// ─── Auth ───────────────────────────────────────────────────────────────────

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ─── Types ──────────────────────────────────────────────────────────────────

interface Schedule {
  schedule_id: string;
  schedule_name: string;
  template_id: string;
  template_name: string;
  asset_type_ids: string[];
  asset_type_names: string[];
  scope_asset_ids: string[];
  scope_asset_names: string[];
  scope_job_tag_ids?: string[];
  location_id: string | null;
  location_name?: string;
  recurrence_type?: string;
  days_of_week: number[];
  day_of_month?: number;
  week_of_month?: number;
  weekday?: number;
  interval_amount?: number;
  interval_unit?: string;
  month_of_year?: number;
  yearly_day?: number;
  time_of_day: string;
  timezone: string;
  enabled: boolean;
  email_notifications_enabled: boolean;
  push_notifications_enabled: boolean;
  send_roundup_email: boolean;
  alert_levels: { alert_level_id: string; minutes_after_scheduled_time: number; alert_type: string; recipients?: any[] }[];
  alert_level_count: number;
}

const ALERT_COLOURS: Record<string, 'warning' | 'error' | 'default'> = {
  warning: 'warning', critical: 'error', urgent: 'error',
};
const LEVEL_COLOR: Record<string, string> = {
  warning: '#D97706', critical: '#DC2626', urgent: '#7C3AED',
};

// ─── Component ──────────────────────────────────────────────────────────────

const SchedulesTab: React.FC = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSchedule, setDrawerSchedule] = useState<ScheduleData | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<Schedule | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Test alert
  const [testingId, setTestingId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Business feature flags for location scoping
  const [bizLocationsEnabled, setBizLocationsEnabled] = useState(true);
  const [bizLocationLabel, setBizLocationLabel] = useState('Location');

  // ── Business feature flags ──────────────────────────────────────────────

  useEffect(() => {
    const bid = localStorage.getItem('businessId');
    const token = localStorage.getItem('token');
    if (!bid || !token) return;
    axios
      .get(`${API_BASE_URL}/businesses/${bid}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setBizLocationsEnabled(res.data.allow_locations !== 0 && res.data.allow_locations !== false);
        setBizLocationLabel(
          res.data.use_site_terminology === 1 || res.data.use_site_terminology === true ? 'Site' : 'Location'
        );
      })
      .catch(() => {/* non-fatal — keep defaults */});
  }, []);

  // ── Fetch ─────────────────────────────────────────────────────────────

  const fetchSchedules = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/template-check-schedules`, { headers: getAuthHeaders() });
      const data = res.data;
      const arr: Schedule[] = Array.isArray(data) ? data : data?.schedules ?? [];
      setSchedules(arr);
    } catch (err: any) {
      // Only attempt the per-template fallback if the primary endpoint is genuinely absent
      // (network error or 404). A 5xx means the server errored — retrying per-template
      // would just make N identical failures and slow the UI to a crawl.
      const status: number | undefined = err?.response?.status;
      if (status && status >= 500) {
        setError('Failed to load schedules. The server returned an error — please try again or contact support.');
        setLoading(false);
        return;
      }
      // 404 / network error: fall back to per-template fetch (legacy route compatibility)
      try {
        const tmplRes = await axios.get(`${API_BASE_URL}/check-templates`, { headers: getAuthHeaders() });
        const templates: any[] = Array.isArray(tmplRes.data) ? tmplRes.data : tmplRes.data?.data ?? [];
        const all: Schedule[] = [];
        await Promise.allSettled(templates.map(async (tmpl) => {
          try {
            const r = await axios.get(`${API_BASE_URL}/template-check-schedules/${tmpl.template_id}`, { headers: getAuthHeaders() });
            const d: any[] = Array.isArray(r.data) ? r.data : r.data?.schedules ?? [];
            d.forEach(s => all.push({
              ...s,
              template_name: tmpl.template_name,
              asset_type_names: s.asset_type_names ?? [],
              scope_asset_ids: s.scope_asset_ids ?? [],
              scope_asset_names: s.scope_asset_names ?? [],
              alert_levels: s.alert_levels ?? [],
              alert_level_count: (s.alert_levels ?? []).length,
            }));
          } catch { /* skip */ }
        }));
        setSchedules(all);
      } catch {
        setError('Failed to load schedules.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);

  // ── Open edit (fetch full detail with recipients) ──────────────────────

  const openEdit = async (s: Schedule) => {
    setDrawerLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/template-check-schedules/by-id/${s.schedule_id}`, { headers: getAuthHeaders() });
      const full = res.data?.schedule ?? res.data;
      setDrawerSchedule(full as ScheduleData);
    } catch {
      // Fallback to lightweight data if route unavailable
      setDrawerSchedule({
        schedule_id: s.schedule_id,
        template_id: s.template_id,
        template_name: s.template_name,
        asset_type_ids: s.asset_type_ids ?? [],
        scope_asset_ids: s.scope_asset_ids ?? [],
        location_id: s.location_id ?? null,
        schedule_name: s.schedule_name,
        recurrence_type: (s.recurrence_type as any) ?? 'weekly',
        days_of_week: s.days_of_week ?? [],
        day_of_month: s.day_of_month ?? 1,
        week_of_month: s.week_of_month ?? 1,
        weekday: s.weekday ?? 1,
        interval_amount: s.interval_amount ?? 2,
        interval_unit: (s.interval_unit as any) ?? 'weeks',
        month_of_year: s.month_of_year ?? 0,
        yearly_day: s.yearly_day ?? 1,
        time_of_day: s.time_of_day,
        timezone: 'Europe/London',
        enabled: s.enabled,
        email_notifications_enabled: s.email_notifications_enabled,
        push_notifications_enabled: s.push_notifications_enabled,
        send_roundup_email: s.send_roundup_email,
        roundup_lookback_amount: 24,
        roundup_lookback_unit: 'hours',
        alert_levels: (s.alert_levels ?? []).map(al => ({ ...al, recipients: al.recipients ?? [] })),
        roundup_recipients: [],
      } as any);
    } finally {
      setDrawerLoading(false);
      setDrawerOpen(true);
    }
  };

  const openNew = () => { setDrawerSchedule(null); setDrawerOpen(true); };

  // ── Enable toggle ──────────────────────────────────────────────────────

  const handleToggleEnabled = async (s: Schedule) => {
    try {
      await axios.put(`${API_BASE_URL}/template-check-schedules/${s.schedule_id}`, { enabled: !s.enabled }, { headers: getAuthHeaders() });
      setSchedules(prev => prev.map(x => x.schedule_id === s.schedule_id ? { ...x, enabled: !s.enabled } : x));
    } catch { /* ignore */ }
  };

  // ── Delete ─────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/template-check-schedules/${deleteTarget.schedule_id}`, { headers: getAuthHeaders() });
      setSchedules(prev => prev.filter(x => x.schedule_id !== deleteTarget.schedule_id));
      setDeleteTarget(null);
    } catch {
      setError('Failed to delete schedule.');
    } finally {
      setDeleting(false);
    }
  };

  // ── Test alert ─────────────────────────────────────────────────────────

  const handleTest = async (s: Schedule) => {
    setTestingId(s.schedule_id);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/template-check-schedules/${s.schedule_id}/test`,
        {},
        { headers: getAuthHeaders() },
      );
      setSnackbar({ open: true, message: res.data?.message || 'Test alert sent', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Failed to send test alert', severity: 'error' });
    } finally {
      setTestingId(null);
    }
  };

  const toggleExpand = (id: string) => {
    setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  // ── Filter ────────────────────────────────────────────────────────────

  const filtered = schedules.filter(s =>
    s.schedule_name.toLowerCase().includes(search.toLowerCase()) ||
    (s.template_name ?? '').toLowerCase().includes(search.toLowerCase())
  );

  // ── Scope summary helper ───────────────────────────────────────────────

  const scopeSummary = (s: Schedule) => {
    if (s.scope_job_tag_ids?.length) return {
      icon: <CategoryIcon sx={{ fontSize: 14, color: '#B45309' }} />,
      label: (s as any).scope_tag_template_ids?.length
        ? `By tag + template (${s.scope_job_tag_ids.length} tag${s.scope_job_tag_ids.length !== 1 ? 's' : ''})`
        : `By tag (${s.scope_job_tag_ids.length})`,
      color: '#B45309', bg: '#FEF3C7',
    };
    if (s.scope_asset_ids?.length > 0) return {
      icon: <PersonIcon sx={{ fontSize: 14, color: '#7C3AED' }} />,
      label: s.scope_asset_names?.length > 0
        ? s.scope_asset_names.slice(0, 2).join(', ') + (s.scope_asset_names.length > 2 ? ` +${s.scope_asset_names.length - 2}` : '')
        : `${s.scope_asset_ids.length} specific assets`,
      color: '#7C3AED', bg: '#F3E8FF',
    };
    if (s.location_id) return {
      icon: <LocationIcon sx={{ fontSize: 14, color: '#0284C7' }} />,
      label: s.location_name ?? 'Location set',
      color: '#0284C7', bg: '#E0F2FE',
    };
    return {
      icon: <CategoryIcon sx={{ fontSize: 14, color: '#059669' }} />,
      label: s.asset_type_names?.slice(0, 2).join(', ') || 'All assets',
      color: '#059669', bg: '#D1FAE5',
    };
  };

  // ─── Recipient summary (for expanded view) ────────────────────────────

  const recipientChips = (recs: any[]) => {
    if (!recs?.length) return <Typography variant="caption" color="text.disabled">No recipients</Typography>;
    return recs.map((r: any, i: number) => {
      const label = r.recipient_type === 'email'
        ? (r.email || r._label || 'Email')
        : (r._label ?? r.user_name ?? r.team_name ?? r.job_tag_name ?? (r.recipient_type === 'user' ? 'User' : r.recipient_type === 'team' ? 'Team' : 'Tag'));
      const icon = r.recipient_type === 'email'
        ? <EmailOutlineIcon sx={{ fontSize: 11 }} />
        : r.recipient_type === 'user'
          ? <PersonIcon sx={{ fontSize: 11 }} />
          : r.recipient_type === 'team'
            ? <TeamIcon sx={{ fontSize: 11 }} />
            : <TagIcon sx={{ fontSize: 11 }} />;
      return (
        <Chip
          key={i}
          icon={icon}
          label={label}
          size="small"
          variant="outlined"
          color={
            r.recipient_type === 'email'
              ? 'info'
              : r.recipient_type === 'user'
                ? 'primary'
                : r.recipient_type === 'team'
                  ? 'secondary'
                  : 'default'
          }
          sx={{ fontSize: '0.7rem', height: 22 }}
        />
      );
    });
  };

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Box sx={commTabHeaderSx}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Schedules</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Schedules define <strong>when</strong> a check template must be completed and <strong>who</strong> gets alerted if it's overdue.
            Choose the assets or asset types the schedule applies to, set a daily/weekly deadline, then add escalating alerts
            that fire minutes after the deadline — notifying managers, teams, or job-tag groups so they can reassign or take action.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew} sx={{ flexShrink: 0 }}>
          New Schedule
        </Button>
      </Box>

      <TextField
        placeholder="Search schedules or templates…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        size="small"
        sx={{ mb: 3, maxWidth: 360 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
        }}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>}

      {!loading && filtered.length === 0 && (
        <CommEmptyState
          icon={<ScheduleIcon />}
          heading={search ? 'No schedules match your search.' : 'No schedules yet.'}
          description={!search ? 'Create a schedule to enforce check deadlines on your assets. When a check is overdue, escalating alerts will notify the right people so they can reassign or take action.' : undefined}
          ctaLabel={!search ? 'Create first schedule' : undefined}
          onCta={!search ? openNew : undefined}
        />
      )}

      {!loading && filtered.length > 0 && (
        <Paper>
          <Table>
            <TableHead>
              <TableRow sx={commTableHeadRowSx}>
                <TableCell width={36} />
                <TableCell>Schedule</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Deadline</TableCell>
                <TableCell align="center">Alert Levels</TableCell>
                <TableCell align="center">Enabled</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(s => {
                const scope = scopeSummary(s);
                const isExpanded = expanded.has(s.schedule_id);
                return (
                  <React.Fragment key={s.schedule_id}>
                    <TableRow hover sx={{ opacity: s.enabled ? 1 : 0.55 }}>
                      {/* Expand toggle */}
                      <TableCell padding="none" sx={{ pl: 0.5 }}>
                        {(s.alert_levels?.length > 0) && (
                          <IconButton size="small" onClick={() => toggleExpand(s.schedule_id)}>
                            {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                          </IconButton>
                        )}
                      </TableCell>

                      {/* Name */}
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{s.schedule_name}</Typography>
                        <Typography variant="caption" color="text.secondary">Europe/London</Typography>
                      </TableCell>

                      {/* Template */}
                      <TableCell>
                        <Typography variant="body2">{s.template_name}</Typography>
                      </TableCell>

                      {/* Scope badge */}
                      <TableCell>
                        <Chip
                          icon={scope.icon}
                          label={scope.label}
                          size="small"
                          sx={{ bgcolor: scope.bg, color: scope.color, fontWeight: 600, border: 'none' }}
                          variant="outlined"
                        />
                      </TableCell>

                      {/* Deadline + recurrence */}
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{s.time_of_day?.slice(0, 5)}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {describeRecurrence(s as any)}
                        </Typography>
                      </TableCell>

                      {/* Alert level count */}
                      <TableCell align="center">
                        {s.alert_levels?.length > 0 ? (
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            {s.alert_levels.map((al, i) => (
                              <Chip
                                key={i}
                                label={`+${al.minutes_after_scheduled_time}m`}
                                size="small"
                                color={ALERT_COLOURS[al.alert_type] ?? 'default'}
                                variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 20 }}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.disabled">None</Typography>
                        )}
                      </TableCell>

                      {/* Enabled toggle */}
                      <TableCell align="center">
                        <Tooltip title={s.enabled ? 'Disable schedule' : 'Enable schedule'}>
                          <Switch size="small" checked={s.enabled} onChange={() => handleToggleEnabled(s)} />
                        </Tooltip>
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                        <Stack spacing={0.75} alignItems="flex-end">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                            <Tooltip title="Edit schedule">
                              <span>
                                <IconButton size="small" onClick={() => openEdit(s)} disabled={drawerLoading || testingId !== null}>
                                  {drawerLoading ? <CircularProgress size={16} /> : <EditIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Delete schedule">
                              <IconButton size="small" color="error" onClick={() => setDeleteTarget(s)} disabled={testingId !== null}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                          <Tooltip title={s.alert_levels?.length === 0 ? 'Add an alert level first to send a test' : 'Send a sample alert to this schedule\'s recipients'}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                fullWidth
                                startIcon={
                                  testingId === s.schedule_id
                                    ? <CircularProgress size={14} color="inherit" />
                                    : <SendIcon sx={{ fontSize: 16 }} />
                                }
                                onClick={() => handleTest(s)}
                                disabled={testingId !== null || s.alert_levels?.length === 0}
                                sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 100, maxWidth: 120 }}
                              >
                                {testingId === s.schedule_id ? 'Sending…' : 'Test alert'}
                              </Button>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* Alert levels expansion — shows timing + recipients */}
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 7, background: '#FAFAFA', borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1.5, display: 'block', textTransform: 'uppercase', letterSpacing: .4 }}>
                              Escalation levels
                            </Typography>
                            <Stack spacing={1.25}>
                              {(s.alert_levels ?? []).map((al, i) => (
                                <Box
                                  key={al.alert_level_id ?? i}
                                  sx={{
                                    display: 'flex', alignItems: 'flex-start', gap: 1.5,
                                    px: 2, py: 1.25,
                                    border: '1px solid', borderColor: (LEVEL_COLOR[al.alert_type] ?? '#6B7280') + '40',
                                    borderRadius: 1.5, background: '#fff',
                                  }}
                                >
                                  <Box sx={{ minWidth: 90, flexShrink: 0 }}>
                                    <Chip
                                      label={al.alert_type}
                                      size="small"
                                      color={ALERT_COLOURS[al.alert_type] ?? 'default'}
                                      variant="filled"
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                      +{al.minutes_after_scheduled_time} min
                                    </Typography>
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                      Recipients
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {recipientChips(al.recipients ?? [])}
                                    </Box>
                                  </Box>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Info callout */}
      {!loading && filtered.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          When a check is overdue, each escalation level fires at its configured delay. Recipients receive email and/or push notifications so they can reassign the check or take action directly in the admin panel.
        </Alert>
      )}

      {/* Schedule drawer */}
      <CommScheduleDrawer
        open={drawerOpen}
        schedule={drawerSchedule}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => { setDrawerOpen(false); fetchSchedules(); }}
        locationsEnabled={bizLocationsEnabled}
        locationLabel={bizLocationLabel}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete schedule?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deleteTarget?.schedule_name}</strong>?
            This will also remove its alert levels, recipients, and round-up links. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SchedulesTab;
